'use client'

import { useEffect, useState } from 'react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'

import { DEFAULT_EDGE_VOICE, EDGE_VOICES } from '@/lib/a11y/edge-voices'
import { pickBestVoice } from '@/lib/a11y/pick-voice'

import { useAccessibility } from '../AccessibilityProvider'

export function ReadingSection() {
  const { settings, update, announce } = useAccessibility()
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [supported, setSupported] = useState(true)

  const isEdge = settings.ttsEngine === 'edge'

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.speechSynthesis === 'undefined' ||
      window.speechSynthesis === null
    ) {
      setSupported(false)
      return
    }
    const load = (): void => {
      setVoices(window.speechSynthesis.getVoices())
    }
    load()
    window.speechSynthesis.addEventListener('voiceschanged', load)
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', load)
    }
  }, [])

  const serbianVoices = voices.filter((v) => v.lang.startsWith('sr') || v.lang.startsWith('hr'))
  const otherVoices = voices.filter((v) => !(v.lang.startsWith('sr') || v.lang.startsWith('hr')))

  return (
    <section aria-labelledby="a11y-reading-heading" className="flex flex-col gap-4">
      <h3 id="a11y-reading-heading" className="text-base font-semibold">
        Čitanje
      </h3>

      <div className="flex items-start justify-between gap-3">
        <Label htmlFor="tts-engine" className="flex flex-1 cursor-pointer flex-col gap-0.5">
          <span>Neural glas (Edge TTS)</span>
          <span className="text-muted-foreground text-xs font-normal">
            Kvalitetniji glas — zahteva internet konekciju
          </span>
        </Label>
        <Switch
          id="tts-engine"
          checked={isEdge}
          onCheckedChange={(checked) => {
            const engine = checked ? 'edge' : 'browser'
            update({ ttsEngine: engine, ttsVoiceName: null })
            announce(`Glas: ${checked ? 'neural (Edge TTS)' : 'pregledač'}`)
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="tts-rate">Brzina čitanja: {settings.ttsRate.toFixed(1)}×</Label>
        <Slider
          id="tts-rate"
          min={0.5}
          max={2}
          step={0.1}
          value={[settings.ttsRate]}
          onValueChange={([val]) => {
            if (typeof val === 'number') {
              update({ ttsRate: val })
            }
          }}
          onValueCommit={([val]) => {
            if (typeof val === 'number') {
              announce(`Brzina čitanja: ${val.toFixed(1)} puta`)
            }
          }}
        />
      </div>

      {isEdge ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="tts-voice-edge">Glas</Label>
          <Select
            value={settings.ttsVoiceName ?? DEFAULT_EDGE_VOICE}
            onValueChange={(val) => {
              update({ ttsVoiceName: val })
              const voice = EDGE_VOICES.find((v) => v.name === val)
              announce(`Glas: ${voice?.label ?? val}`)
            }}
          >
            <SelectTrigger id="tts-voice-edge">
              <SelectValue placeholder="Izaberi glas" />
            </SelectTrigger>
            <SelectContent>
              {EDGE_VOICES.map((v) => (
                <SelectItem key={v.name} value={v.name}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Label htmlFor="tts-voice">Glas</Label>
          {!supported ? (
            <p className="text-muted-foreground text-sm">
              Tvoj pregledač ne podržava čitanje naglas (Web Speech API). Probaj Chrome, Edge ili
              Safari.
            </p>
          ) : (
            <>
              <Select
                value={settings.ttsVoiceName ?? 'default'}
                onValueChange={(val) => {
                  const name = val === 'default' ? null : val
                  update({ ttsVoiceName: name })
                  announce(`Glas: ${val === 'default' ? 'podrazumevani' : val}`)
                }}
              >
                <SelectTrigger id="tts-voice">
                  <SelectValue placeholder="Izaberi glas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Podrazumevani</SelectItem>
                  {serbianVoices.map((v) => (
                    <SelectItem key={v.name} value={v.name}>
                      {v.name} ({v.lang})
                    </SelectItem>
                  ))}
                  {otherVoices.slice(0, 10).map((v) => (
                    <SelectItem key={v.name} value={v.name}>
                      {v.name} ({v.lang})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const active = pickBestVoice(voices, settings.ttsVoiceName)
                if (!active) return null
                if (settings.ttsVoiceName === active.name) return null
                return (
                  <p className="text-muted-foreground text-xs">
                    Trenutno aktivan: <strong>{active.name}</strong> ({active.lang})
                  </p>
                )
              })()}
              {serbianVoices.length === 0 && (
                <p className="text-muted-foreground text-xs">
                  Tvoj sistem nema srpski glas — uključi Neural glas iznad ili instaliraj dodatne
                  glasove u podešavanjima OS-a.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <Label htmlFor="tts-karaoke" className="flex flex-1 cursor-pointer flex-col gap-0.5">
          <span>Karaoke isticanje</span>
          <span className="text-muted-foreground text-xs font-normal">
            Istakni trenutno izgovorenu reč tokom čitanja
          </span>
        </Label>
        <Switch
          id="tts-karaoke"
          checked={settings.ttsKaraoke}
          onCheckedChange={(checked) => {
            update({ ttsKaraoke: checked })
            announce(`Karaoke isticanje: ${checked ? 'uključeno' : 'isključeno'}`)
          }}
        />
      </div>
    </section>
  )
}
